import { useDashboardAudience } from './dashboardAudience'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ApiError, getJSON } from '@/lib/api'
import type { MemberPortalOrganization } from './MemberSignIn'
import type { PortalSpendResponse } from './portalSpend.types'
import { usePortalSpendState } from './usePortalSpendState'
import { usePortalSpendDownload } from './usePortalSpendDownload'
import { PortalSpendControls, PortalSpendStats } from './PortalSpendControls'
import { PortalSpendTable } from './PortalSpendTable'
import { PortalPagination } from './PortalPagination'

export function MemberPortalSpend({
  organization,
  viewer,
}: {
  organization: MemberPortalOrganization
  viewer: string
}) {
  const audience = useDashboardAudience(organization.id)
  const state = usePortalSpendState(organization.id)
  const query = useQuery({
    queryKey: [
      audience.preview ? 'organization-dashboard-preview' : 'member-portal-dashboard',
      viewer,
      organization.id,
      'spend',
      state.path,
    ],
    queryFn: () => getJSON<PortalSpendResponse>(state.path),
    retry: false,
  })
  const csv = usePortalSpendDownload(state.csvPath)
  const data = query.isError ? undefined : query.data?.spend
  if (
    !audience.preview &&
    (csv.forbidden || (query.error instanceof ApiError && query.error.status === 404))
  )
    return <Navigate to="/portal" replace />
  return (
    <Card className="relative overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <h2 className="text-body font-medium">Spend</h2>
            {data && (
              <span className="text-caption text-muted-foreground">
                · {data.total} invoices · {data.net.formatted}
              </span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5"
            disabled={csv.pending || !data}
            onClick={() => void csv.download()}
          >
            <Download aria-hidden="true" className="size-3.5" />
            CSV
          </Button>
        </div>
        <PortalSpendControls params={state.params} change={state.change} data={data} />
      </div>
      {csv.error && <Alert variant="destructive">CSV unavailable. Try downloading again.</Alert>}
      {query.isPending && <EmptyState variant="quiet" title="Loading invoices" />}
      {query.isError && (
        <Alert variant="destructive">
          Spend unavailable.{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {data && <SpendResults data={data} state={state} organization={organization} />}
    </Card>
  )
}

function SpendResults({
  data,
  state,
  organization,
}: {
  data: PortalSpendResponse['spend']
  state: ReturnType<typeof usePortalSpendState>
  organization: MemberPortalOrganization
}) {
  return (
    <>
      <PortalSpendStats name={organization.name} stats={data.stats} />
      {data.rows.length ? (
        <PortalSpendTable
          rows={data.rows}
          sort={state.params.get('sort')!}
          onSort={(sort) => state.change('sort', sort)}
        />
      ) : (
        <EmptyState
          variant="quiet"
          title={data.available_total ? 'No rows match' : 'No invoices yet'}
          action={
            data.available_total ? (
              <Button variant="outline" onClick={state.clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}
      <PortalPagination
        pagination={data.pagination}
        onPage={(cursor) => state.change('cursor', cursor)}
      />
    </>
  )
}
